import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ExternalLink, FileText } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface SpaDocument {
  id: string;
  title: string;
  description: string | null;
  doc_type: string;
  spa_pillar: string | null;
  language: string;
  file_url: string | null;
  external_url: string | null;
  created_at: string;
}

const DOC_TYPES = ['guideline', 'communique', 'report', 'other'] as const;
const PILLARS = ['critical_minerals', 'industrialization', 'infrastructure', 'governance'] as const;

const PILLAR_BADGE: Record<string, string> = {
  critical_minerals: 'bg-spa-minerals/10 text-spa-minerals border-spa-minerals/30',
  industrialization: 'bg-spa-industrialization/10 text-spa-industrialization border-spa-industrialization/30',
  infrastructure: 'bg-spa-infrastructure/10 text-spa-infrastructure border-spa-infrastructure/30',
  governance: 'bg-spa-governance/10 text-spa-governance border-spa-governance/30',
};

export default function DocumentsLibrary() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [documents, setDocuments] = useState<SpaDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [pillarFilter, setPillarFilter] = useState<string>('all');
  const [langFilter, setLangFilter] = useState<string>('all');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('spa_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments((data as SpaDocument[]) || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = documents.filter(d => {
    if (typeFilter !== 'all' && d.doc_type !== typeFilter) return false;
    if (pillarFilter !== 'all' && d.spa_pillar !== pillarFilter) return false;
    if (langFilter !== 'all' && d.language !== langFilter) return false;
    return true;
  });

  const getDocUrl = (d: SpaDocument) => d.external_url || d.file_url || null;
  const getLangDisplay = (lang: string) => (lang === 'fr' ? '🇫🇷 FR' : '🇬🇧 EN');

  const ActionButton = ({ doc }: { doc: SpaDocument }) => {
    const url = getDocUrl(doc);
    if (url) {
      return (
        <Button asChild variant="outline" size="sm" className="gap-2">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3.5 w-3.5" />
            {t('documents.viewDownload')}
          </a>
        </Button>
      );
    }
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="sm" disabled className="gap-2">
            <ExternalLink className="h-3.5 w-3.5" />
            {t('documents.viewDownload')}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('misc.noFileAvailable')}</TooltipContent>
      </Tooltip>
    );
  };

  const PillarBadge = ({ pillar }: { pillar: string | null }) => {
    if (!pillar || !PILLAR_BADGE[pillar]) return <span className="text-muted-foreground text-xs">—</span>;
    return (
      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${PILLAR_BADGE[pillar]}`}>
        {t(`spaPillars.${pillar}`)}
      </span>
    );
  };

  return (
    <Layout>
      <div className="container py-8">
        <h1 className="text-3xl font-bold">{t('documents.title')}</h1>
        <p className="mt-2 mb-8 text-muted-foreground">{t('documents.description')}</p>

        {/* Filter bar */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder={t('documents.filterType')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('documents.filterType')}: All</SelectItem>
              {DOC_TYPES.map(dt => (
                <SelectItem key={dt} value={dt}>{dt.charAt(0).toUpperCase() + dt.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={pillarFilter} onValueChange={setPillarFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder={t('programs.filterPillar')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('programs.filterPillar')}: All</SelectItem>
              {PILLARS.map(p => (
                <SelectItem key={p} value={p}>{t(`spaPillars.${p}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex gap-1">
            {(['all', 'en', 'fr'] as const).map(lang => (
              <Button
                key={lang}
                variant={langFilter === lang ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLangFilter(lang)}
              >
                {lang === 'all' ? 'All' : lang === 'en' ? '🇬🇧 EN' : '🇫🇷 FR'}
              </Button>
            ))}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-16 w-16 text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">{t('documents.noResults')}</p>
          </div>
        ) : isMobile ? (
          /* Mobile: stacked cards */
          <div className="space-y-4">
            {filtered.map(doc => (
              <Card key={doc.id}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-sm">{doc.title}</h3>
                      {doc.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{doc.description}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {doc.doc_type}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <PillarBadge pillar={doc.spa_pillar} />
                    <span className="text-xs">{getLangDisplay(doc.language)}</span>
                  </div>
                  <ActionButton doc={doc} />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* Desktop: table */
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%]">Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Pillar</TableHead>
                  <TableHead>{t('documents.filterLanguage')}</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(doc => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <p className="font-medium">{doc.title}</p>
                      {doc.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{doc.description}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{doc.doc_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <PillarBadge pillar={doc.spa_pillar} />
                    </TableCell>
                    <TableCell className="text-sm">{getLangDisplay(doc.language)}</TableCell>
                    <TableCell className="text-right">
                      <ActionButton doc={doc} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Layout>
  );
}
